import { differenceInDays, format, isValid, parseISO } from 'date-fns';
import { capitalize, isEmpty, isNil, some, sortBy, sum } from 'lodash';
import { Link } from 'react-router-dom';

import { USStateDisplayNameMapping } from '@simplifidev/shared/dist/constants/states';

import Badge from 'components/Badge';
import {
  LOAN_TYPE_FSA,
  LOAN_TYPE_PLAID_FEDERAL,
  SAVE_PLAN_TYPE,
  isIdr,
  isPslf,
} from 'utils/constants';
import {
  avgInterestRate,
  dollars,
  formatPhone,
  isCaresActEligible,
  rateParser,
  yesOrNo,
} from 'utils/general';

import type React from 'react';

export const getWindDownStatus = ({ borrower }: any) => {
  const windDown = borrower?.windDown;

  if (!windDown) {
    return {
      currentStatus: null,
      statusLabel: null,
      badgeColorName: null,
      windDownStatuses: [],
      latestWindDownStart: null,
      latestWindDownEnd: null,
    };
  }

  const { currentStatus, windDownStatuses = [] } = windDown;

  const latestStatus = windDownStatuses[0];

  const calculateDaysLeft = (endDate: any) => {
    if (!endDate) {
      return null;
    }
    const today = new Date();
    const end = parseISO(endDate);
    const daysLeft = differenceInDays(end, today);
    return Math.max(0, daysLeft);
  };

  const getStatusConfig = (status: any, endDate: any) => {
    switch (status) {
      case 'wind-down': {
        const daysLeft = calculateDaysLeft(endDate);
        return {
          label:
            daysLeft !== null
              ? `In Wind-down (${daysLeft} days left)`
              : 'In Wind-down',
          colorName: 'sand',
        };
      }
      case 'inactive':
        return {
          label: 'Inactive',
          colorName: 'raven',
        };
      default:
        return {
          label: null,
          colorName: null,
        };
    }
  };

  const config = getStatusConfig(currentStatus, latestStatus?.windDownEndsDo);

  return {
    currentStatus,
    statusLabel: config.label,
    badgeColorName: config.colorName,
    windDownStatuses,
    latestWindDownStart: latestStatus?.windDownStartsDo || null,
    latestWindDownEnd: latestStatus?.windDownEndsDo || null,
  };
};

export type ThrottleData =
  | { _tag: 'NotThrottled'; failures: number }
  | { _tag: 'Throttled'; blockedUntil: string; failures: number }
  | { _tag: 'Unavailable' };

const formatThrottleTimestamp = (value: string | number | undefined) => {
  if (isNil(value)) return '-';
  const date = typeof value === 'number' ? new Date(value) : parseISO(value);
  return isValid(date) ? format(date, 'LL/dd/yyyy, hh:mm a') : '-';
};

const describeFailures = (failures: unknown) => {
  if (typeof failures !== 'number' || !Number.isFinite(failures)) return null;
  return `${String(failures)} failed ${failures === 1 ? 'attempt' : 'attempts'}`;
};

/**
 * The throttle state comes from a separate store that the API reports as
 * `Unavailable` when it can't be read. Anything we can't interpret (missing,
 * unknown tag, malformed fields) reads as unavailable rather than throwing, so
 * the rest of the borrower page still renders.
 */
const throttleRow = (
  throttle: ThrottleData | null | undefined,
  checkedAt: number | undefined,
) => {
  const withFailures = (label: string, failures: unknown) => {
    const count = describeFailures(failures);
    return count ? `${label} · ${count}` : label;
  };

  let status = 'Unavailable';
  let blockedUntil = '-';
  switch (throttle?._tag) {
    case 'Throttled':
      status = withFailures('Throttled', throttle.failures);
      blockedUntil = formatThrottleTimestamp(
        typeof throttle.blockedUntil === 'string'
          ? throttle.blockedUntil
          : undefined,
      );
      break;
    case 'NotThrottled':
      status =
        throttle.failures > 0
          ? withFailures('Not throttled', throttle.failures)
          : 'Not throttled';
      break;
    default:
      break;
  }

  return [
    { label: 'Login Throttle', value: status },
    { label: 'Blocked Until', value: blockedUntil },
    { label: 'Throttle Checked At', value: formatThrottleTimestamp(checkedAt) },
  ];
};

/**
 * Build high-level info rows for a borrower.
 *
 * A "borrower" here is the response `data` object returned by the GET Borrower
 * route (function `getBorrower`) in `packages/admin-server/src/routes/borrowers.ts`.
 * Only the fields referenced by this function are documented here.
 * @todo This should probably come from an API file rather than being defined here.
 */
type Borrower = {
  id: number | string;
  email: string;
  partnerName: string | null;
  parentUser?: {
    id: number | string;
    firstName: string;
    lastName: string;
  } | null;
  forms: object[];
  loans: object[];
  plaidLoans: object[];
  lastLogin: string | Date | null;
  isVerified: boolean | null;
  isUserRMEligible: boolean;
  createdAt: string | Date | null;
  retirementMatchOptedOutAt: string | Date | null;
  socialSSO: boolean;
  stripeMembership?: { status?: 'payment_saved' };
  retirementMatchStartedAt: string | Date | null;
  partnerSSO: boolean;
  retirementMatchConsentedAt: string | Date | null;
  currentlyNycEmployee: boolean | null;
  workEmail: string | null;
  workEmailLastVerifiedAt: string | Date | null;
  usesWorkEmailOtp: boolean;
  throttle?: ThrottleData | null;
  residencyVerification: {
    verifiedAt?: number | string | Date | null;
    isRequired: unknown;
    verificationMethod: unknown;
  };
  windDown?: object[];
};
export const borrowerHighInfoData = ({
  borrower,
  throttleCheckedAt,
}: {
  borrower: Borrower;
  throttleCheckedAt?: number;
}) => {
  const formatDate = (date: string | Date | null) => {
    const iso = date instanceof Date ? date.toISOString() : date;
    if (isNil(iso)) {
      return '-';
    }
    return format(parseISO(iso), 'LL/dd/yyyy, hh:mm a');
  };
  const formatDateOnly = (date: number | Date) =>
    !isNil(date) ? format(date, 'yyyy-MM-dd') : '-';
  const formatISODateOnly = (date: string) =>
    !isNil(date) ? format(parseISO(date), 'yyyy-MM-dd') : '-';

  const windDownStatus = getWindDownStatus({ borrower });
  /** Forms are a little more complicated to type so I'm leaving the any here for now until we can derive their typing from the backend. */
  const isCompletedIDR = (forms: any[]) =>
    forms?.some(
      form =>
        isIdr(form.formType) &&
        (form.status === 'signed' || form.status === 'processed'),
    )
      ? 'Yes'
      : 'No';

  /** Forms are a little more complicated to type so I'm leaving the any here for now until we can derive their typing from the backend. */
  const isCompletedPSLF = (forms: any[]) =>
    forms?.some(
      form =>
        isPslf(form.formType) &&
        (form.status === 'signed' || form.status === 'processed'),
    )
      ? 'Yes'
      : 'No';

  /** Forms are a little more complicated to type so I'm leaving the any here for now until we can derive their typing from the backend. */
  const isConsolidation = (forms: any[]) =>
    forms?.some(form => form.form.isConsolidation) ? 'Yes' : 'No';

  const paymentDetailsSaved = (
    stripeMembership: { status?: 'payment_saved' } | undefined,
  ) => (stripeMembership?.status === 'payment_saved' ? 'Yes' : 'No');

  const currentlyNycEmployeeResponse = () => {
    switch (borrower.currentlyNycEmployee) {
      case true:
        return 'Yes';
      case false:
        return 'No';
      default:
        return '-';
    }
  };

  type RenderData = { label: string; value: React.ReactNode };

  const { parentUser } = borrower;
  const isHhsUser = parentUser !== null;

  const parentUserValue: React.ReactNode = parentUser ? (
    <Link to={`/borrowers/${parentUser.id}`}>
      {parentUser.firstName} {parentUser.lastName}
    </Link>
  ) : (
    '-'
  );

  const partnerValue = isHhsUser ? '-' : (borrower.partnerName ?? '-');

  const data: RenderData[][] = [
    [{ label: 'Parent User', value: parentUserValue }],
    [
      { label: 'Borrower ID', value: borrower.id },
      { label: 'Partner', value: partnerValue },
      { label: 'Completed IDR', value: isCompletedIDR(borrower.forms) },
    ],
    [
      { label: 'Email', value: borrower.email },
      {
        label: 'Synced FSA',
        value: !isEmpty(borrower.loans) ? 'Yes' : 'No',
      },
      {
        label: 'Synced Plaid',
        value: !isEmpty(borrower.plaidLoans) ? 'Yes' : 'No',
      },
    ],
    [
      { label: 'Last Log In', value: formatDate(borrower.lastLogin) },
      {
        label: 'Email verified',
        value: isNil(borrower.isVerified) ? '-' : yesOrNo(borrower.isVerified),
      },
      {
        label: 'RM Enabled',
        value: yesOrNo(borrower.isUserRMEligible),
      },
    ],
    throttleRow(borrower.throttle, throttleCheckedAt),
    [
      { label: 'Account created', value: formatDate(borrower.createdAt) },
      { label: 'Completed PSLF', value: isCompletedPSLF(borrower.forms) },
      {
        label: 'Opted out of RM',
        value: formatDate(borrower.retirementMatchOptedOutAt),
      },
    ],
    [
      { label: 'Social SSO', value: yesOrNo(borrower.socialSSO) },
      {
        label: 'Payment details saved',
        value: paymentDetailsSaved(borrower.stripeMembership),
      },
      {
        label: 'RM Start Date',
        value: borrower.retirementMatchStartedAt
          ? formatDateOnly(new Date(borrower.retirementMatchStartedAt))
          : '-',
      },
    ],
    [
      { label: 'Partner SSO', value: yesOrNo(borrower.partnerSSO) },
      {
        label: 'Consented to RM',
        value: formatDate(borrower.retirementMatchConsentedAt),
      },
    ],
    [
      {
        label: 'Wind-down Start Date',
        value: formatISODateOnly(windDownStatus.latestWindDownStart),
      },
      {
        label: 'Wind-down End Date',
        value: formatISODateOnly(windDownStatus.latestWindDownEnd),
      },
      {
        label: 'Currently NYC Employee',
        value: currentlyNycEmployeeResponse(),
      },
    ],
    [
      { label: 'Work Email', value: borrower.workEmail ?? '-' },
      {
        label: 'Work Email Last Verified',
        value: formatDate(borrower.workEmailLastVerifiedAt),
      },
      {
        label: 'Uses Work Email OTP',
        value: yesOrNo(borrower.usesWorkEmailOtp),
      },
    ],
  ];

  const isVerified = !!borrower.residencyVerification.verifiedAt;
  const verifiedAt = borrower.residencyVerification.verifiedAt
    ? new Date(borrower.residencyVerification.verifiedAt)
    : null;

  const residencyRow: RenderData[] = [
    { label: 'Consolidation', value: isConsolidation(borrower.forms) },
  ];
  if (borrower.residencyVerification.isRequired) {
    residencyRow.push({
      label: 'Residency Verification',
      value: renderBadge(
        borrower.residencyVerification.verificationMethod,
        isVerified,
      ),
    });
    residencyRow.push({
      label: 'Residency Verification Date',
      value:
        isVerified && verifiedAt ? verifiedAt.toDateString().substring(4) : '-',
    });
  }

  data.push(residencyRow);
  return [{ data }];
};

const renderBadge = (verificationMethod: any, isVerified: any) => {
  const verificationMethodMap = {
    'twilio-zipcode': 'Twilio (phone + ZIP)',
    'admin-override': 'MST Bypass',
    'stripe': 'Stripe (card + ZIP)', // placeholder
  };

  const badgeName =
    (verificationMethodMap as any)[verificationMethod] || 'Unverified';
  return <Badge variant={isVerified ? 'success' : 'error'}>{badgeName}</Badge>;
};

export const borrowerOnboardingInfoData = ({ borrower }: any) => {
  return [
    {
      title: 'Borrower Info',
      data: [
        [
          {
            label: 'Education',
            value: capitalize(borrower.profile.gradStatus),
          },
          {
            label: 'Expected Household gross Income',
            value: dollars(borrower.salary),
          },
          {
            label: 'State',
            value: (USStateDisplayNameMapping as any)[borrower.profile.state],
          },
          {
            label: 'Credit Score',
            value: isNil(borrower.creditScore) ? '-' : borrower.creditScore,
          },
          {
            label: 'Employer',
            value: !isNil(borrower.profile?.currentEmployer)
              ? borrower.profile.currentEmployer
              : '-',
          },
        ],
        [
          {
            label: 'Trouble With Payments',
            value: isNil(borrower.troubleWithPayments)
              ? '-'
              : capitalize(borrower.troubleWithPayments),
          },
          {
            label: 'Family Size',
            value: !isNil(borrower.familyCount) ? borrower.familyCount : '-',
          },
          {
            label: 'Non Profit/public sector?',
            value: !isNil(borrower.isPublicSector)
              ? capitalize(borrower.isPublicSector)
              : '-',
          },
          {
            label: 'Cell Phone',
            value: formatPhone(borrower.profile?.phone),
          },
          {
            label: 'Permission to SMS',
            value: !isNil(borrower.hasOptedIntoPhoneContact)
              ? yesOrNo(borrower.hasOptedIntoPhoneContact)
              : '-',
          },
        ],
      ],
    },
  ];
};

export const borrowerOnboardingLoanInfoData = ({ borrower }: any) => {
  return [
    {
      title: 'Loan Info',
      data: [
        [
          {
            label: 'Federal Loans',
            value: isNil(borrower.federalLoansEstimate)
              ? '-'
              : dollars(borrower.federalLoansEstimate),
          },
          {
            label: 'Federal In Default?',
            value: isNil(borrower.loansInDefaultFederal)
              ? '-'
              : capitalize(borrower.loansInDefaultFederal),
          },
          {
            label: 'In IDR?',
            value: isNil(borrower.isCurrentlyEnrolledInIdr)
              ? '-'
              : capitalize(borrower.isCurrentlyEnrolledInIdr),
          },
          {
            label: 'Private Loans',
            value: isNil(borrower.privateLoansEstimate)
              ? '-'
              : dollars(borrower.privateLoansEstimate),
          },
          {
            label: 'Private In default',
            value: isNil(borrower.loansInDefaultPrivate)
              ? '-'
              : capitalize(borrower.loansInDefaultPrivate),
          },
        ],
      ],
    },
  ];
};

export const borrowerLoanSummaryData = ({ borrower }: any, type: any) => {
  const loans = (type: any) => {
    if (type === LOAN_TYPE_FSA) {
      return borrower.loans;
    }
    if (type === LOAN_TYPE_PLAID_FEDERAL) {
      return borrower.plaidLoans.filter((item: any) => item.isFederal);
    }
    return borrower.plaidLoans.filter((item: any) => !item.isFederal);
  };
  const activeLoans: any[] = loans(type).filter(
    (loan: any) => loan.principalBalance,
  );
  const weigthedAverageInterestRate = (loans: any) => {
    const weightedAverage = avgInterestRate(loans);

    return isEmpty(loans) ? '-' : rateParser(weightedAverage);
  };

  const totalBalance = (loans: any[]) => {
    if (isEmpty(loans)) {
      return '-';
    }
    const totalBalanceArray = activeLoans.map(item => {
      const outstanding = item.outstandingInterestBalance ?? 0;
      // eslint-disable-next-line @typescript-eslint/restrict-plus-operands -- TODO: Type this function properly.
      return item.principalBalance + outstanding;
    });

    return dollars(sum(totalBalanceArray));
  };

  const monthlyPayment = (loans: any[]) => {
    if (isEmpty(loans)) {
      return '-';
    }
    const isMissingData = some(activeLoans, ['monthlyPayment', null]);

    if (isMissingData) {
      return '-';
    }
    const totalMonthlyPayment = sum(
      activeLoans.map(item => item.monthlyPayment),
    );

    return dollars(totalMonthlyPayment);
  };

  const getActiveLoanCount = (loans: any[]) => {
    if (isEmpty(loans)) {
      return 0;
    }
    return loans?.filter(loan => loan.principalBalance !== 0).length;
  };
  return [
    {
      title: 'Loan Info',
      data: [
        [
          {
            label: `Active Loans`,
            value: getActiveLoanCount(loans(type)),
          },
          {
            label: `Total Outstanding Balance`,
            value: totalBalance(loans(type)),
          },
          {
            label: 'Monthly Payment?',
            value: monthlyPayment(loans(type)),
          },
          {
            label: 'Weighted Avg. Interest Rate',
            value: weigthedAverageInterestRate(loans(type)),
          },
        ],
      ],
    },
  ];
};

const getIncludedInIdrLabel = (value: boolean | null | undefined): string => {
  if (value === true) return '✅ Yes';
  if (value === false) return '❌ No';
  return '-';
};

export const borrowerLoanDetailData = (
  { borrower }: any,
  type: any,
  isPaid: any = null,
) => {
  const formatDate = (date: any) =>
    !isNil(date) ? format(parseISO(date), 'LLLL yyyy') : null;
  const getIsFederal = (item: any) => (item.isFederal ? 'Federal' : 'Private');
  return type === LOAN_TYPE_FSA
    ? (borrower.loans as any[])
        ?.filter(item =>
          !isPaid ? item.principalBalance !== 0 : item.principalBalance === 0,
        )
        .map(item => {
          return {
            loanInfo: !isNil(item.type) ? item.type : '-',
            amountBorrowed: dollars(item.disbursedAmount),
            servicer: !isNil(item.servicerName) ? item.servicerName : '-',
            repaymentPlan: !isNil(item.repaymentPlanName)
              ? item.repaymentPlanName
              : '-',
            repaymentBeginDate: !isNil(item.repaymentBeginDate)
              ? item.repaymentBeginDate
              : '-',

            principalBalance: !isNil(item.principalBalance)
              ? dollars(item.principalBalance)
              : '-',
            loanStatus: !isNil(item.loanStatusName) ? item.loanStatusName : '-',
            monthlyPayment: !isNil(item.monthlyPayment)
              ? dollars(item.monthlyPayment)
              : '-',
            recertDate: !isNil(item.idrRecertificationDate)
              ? item.idrRecertificationDate
              : '-',
            outstandingInterest: !isNil(item.outstandingInterestBalance)
              ? dollars(item.outstandingInterestBalance)
              : '-',
            interestRate: !isNil(item.interestRate)
              ? rateParser(item.interestRate)
              : '-',
            loanSource: !isNil(item.source) ? item.source : '-',
            qualifyingPayments: !isNil(item.pslfMatchedMonths)
              ? item.pslfMatchedMonths
              : '-',
            includedInIdr: getIncludedInIdrLabel(
              (item as { includedInIdrCalcs?: boolean | null })
                .includedInIdrCalcs,
            ),
            planEdited: isNil(item.planEdited) ? '-' : yesOrNo(item.planEdited),
            caresAct: isCaresActEligible(
              item.loanType,
              ((item.servicerInformationRaw || []) as any[]).map(
                info => info.name,
              ),
            ),
            createdAt: isNil(item.originatedAt)
              ? null
              : formatDate(item.originatedAt),
          };
        })
    : (borrower.plaidLoans as any[])
        .filter(item =>
          type === LOAN_TYPE_PLAID_FEDERAL ? item.isFederal : !item.isFederal,
        )
        ?.filter(item =>
          !isPaid ? item.principalBalance !== 0 : item.principalBalance === 0,
        )

        .map(item => {
          return {
            loanInfo: !isNil(item.loanName) ? item.loanName : '-',
            amountBorrowed: dollars(item.disbursedAmount),
            servicer: !isNil(item.servicerName) ? item.servicerName : '-',
            repaymentPlan: !isNil(item.repaymentPlanName)
              ? item.repaymentPlanName
              : '-',
            principalBalance: !isNil(item.principalBalance)
              ? dollars(item.principalBalance)
              : '-',
            loanStatus: !isNil(item.loanStatusName) ? item.loanStatusName : '-',
            monthlyPayment: !isNil(item.monthlyPayment)
              ? dollars(item.monthlyPayment)
              : '-',
            isFederal: !isNil(item.isFederal) ? getIsFederal(item) : '-',
            dateBorrowed: !isNil(item.dateBorrowed) ? item.dateBorrowed : '-',
            balance: !isNil(item.currentBalance) ? item.currentBalance : '-',
            // We dont get this from Plaid
            repaymentBeginDate: '-',

            outstandingInterest: !isNil(item.outstandingInterestBalance)
              ? dollars(item.outstandingInterestBalance)
              : '-',
            interestRate: !isNil(item.interestRate)
              ? rateParser(item.interestRate)
              : '-',
            isActive: !isNil(item.active) ? item.active : '-',
            // We dont get this from Plaid
            pslfMatchedMonths: '-',
            createdAt: isNil(item.createdAt)
              ? null
              : formatDate(item.createdAt),
          };
        });
};

export const borrowerPaidLoanDetailData = ({ borrower }: any, type: any) => {
  const borrowerClone = { ...borrower };
  borrowerClone.loans = (borrower.loans as any[])?.filter(
    item => item.principalBalance === 0,
  );
  borrowerClone.plaidLoans = (borrower.plaidLoans as any[])?.filter(
    item => item.principalBalance === 0,
  );
  return borrowerLoanDetailData({ borrower: borrowerClone }, type, true);
};

export const borrowerTuayInfo = ({ borrower }: any) => {
  const mostRecentIDR = (borrower.forms as any[]).find(
    item => item.formType === 'idr',
  );
  const IDRForms = (borrower.forms as any[]).filter(
    item => item.formType === 'idr',
  );

  return [
    {
      title: 'borrower Tuay Info',
      data: [
        [
          {
            label: 'Marital status',
            value: !isNil(borrower.financialProfile?.maritalStatus)
              ? capitalize(borrower.financialProfile.maritalStatus)
              : '-',
          },
          {
            label: 'Dependents (legacy field)',
            value: !isNil(borrower.financialProfile?.numberOfDependents)
              ? borrower.financialProfile.numberOfDependents
              : '-',
          },
          {
            label: 'Kids (legacy field)',
            value: !isNil(borrower.financialProfile?.numberOfKids)
              ? borrower.financialProfile.numberOfKids
              : '-',
          },
          {
            label: 'Tax Dependents',
            value: !isNil(borrower.financialProfile?.numberOfTaxDependents)
              ? borrower.financialProfile.numberOfTaxDependents
              : '-',
          },
          {
            label: 'Additional Household Dependents',
            value: !isNil(
              borrower.financialProfile?.numberOfAdditionalHouseholdDependents,
            )
              ? borrower.financialProfile.numberOfAdditionalHouseholdDependents
              : '-',
          },
          {
            label: 'File jointly with spouse?',
            value: !isNil(borrower.financialProfile?.jointTaxReturn)
              ? capitalize(borrower.financialProfile.jointTaxReturn)
              : '-',
          },
          {
            label: 'Income changed?',
            value: !isNil(borrower.financialProfile?.changeInIncome)
              ? capitalize(borrower.financialProfile.changeInIncome)
              : '-',
          },
        ],
        [
          {
            label: 'AGI (last year)',
            value: !isNil(borrower.financialProfile.adjustedGrossIncome)
              ? dollars(borrower.financialProfile.adjustedGrossIncome)
              : '-',
          },
          {
            label: 'AGI (expected)',
            value: !isNil(borrower.financialProfile?.expectedAnnualGrossIncome)
              ? dollars(borrower.financialProfile.expectedAnnualGrossIncome)
              : '-',
          },
          {
            label: 'Joint AGI (last year)',
            value: !isNil(borrower.financialProfile.jointAdjustedGrossIncome)
              ? dollars(borrower.financialProfile.jointAdjustedGrossIncome)
              : '-',
          },
          {
            label: 'Currently employed?',
            value: !isNil(borrower.financialProfile.currentlyEmployed)
              ? yesOrNo(borrower.financialProfile.currentlyEmployed)
              : '-',
          },
          {
            label: 'Pay frequency',
            value:
              !isEmpty(IDRForms) && !isNil(mostRecentIDR?.form?.payFrequency)
                ? capitalize(mostRecentIDR.form.payFrequency)
                : '-',
          },
          {
            label: 'Perkins forgiveness?',
            value:
              !isEmpty(IDRForms) && !isNil(mostRecentIDR.form.userWillPursuePlc)
                ? yesOrNo(mostRecentIDR.form.userWillPursuePlc)
                : '-',
          },
          {
            label: 'Served in military?',
            value:
              !isEmpty(IDRForms) && !isNil(mostRecentIDR.form.servedInMilitary)
                ? yesOrNo(mostRecentIDR.form.servedInMilitary)
                : '-',
          },
        ],

        [
          {
            label: 'Servicer for consolidation',
            value:
              !isEmpty(IDRForms) &&
              !isNil(mostRecentIDR.form.targetServicerName)
                ? mostRecentIDR.form.targetServicerName
                : '-',
          },
          {
            label: 'Were any loans used for grad school?',
            value: !isNil(borrower.financialProfile.hasLoansFromGradSchool)
              ? borrower.financialProfile.hasLoansFromGradSchool
              : '-',
          },
          {
            label: 'Are you currently in IDR?',
            value: !isNil(borrower.isCurrentlyEnrolledInIdr)
              ? capitalize(borrower.isCurrentlyEnrolledInIdr)
              : '-',
          },
          {
            label: 'Which IDR plan',
            value: borrower?.financialProfile?.repaymentPlan?.shortName ?? '-',
          },

          {
            label: 'IDR Recertification Date',
            value: !isNil(borrower.financialProfile.idrRecertDate)
              ? format(
                  parseISO(borrower.financialProfile.idrRecertDate),
                  'LL dd, yy',
                )
              : '-',
          },

          {
            label: 'Consolidated Loans Include Parent Plus',
            value: !isNil(
              borrower.financialProfile.consolidationIncludedParentPlus,
            )
              ? yesOrNo(
                  borrower.financialProfile.consolidationIncludedParentPlus,
                )
              : '-',
          },
        ],
      ],
    },
  ];
};

export const borrowerUnemploymentInfo = ({ borrower }: any) => {
  return [
    {
      title: 'Unemployment Info',
      data: [
        [
          {
            label: 'Unemployment income',
            value: !isNil(borrower.financialProfile?.unemploymentIncome)
              ? dollars(borrower.financialProfile.unemploymentIncome * 12)
              : '-',
          },
          {
            label: 'Monthly unemployment income',
            value: !isNil(borrower.financialProfile?.unemploymentIncome)
              ? dollars(borrower.financialProfile.unemploymentIncome)
              : '-',
          },
          {
            label: 'Unemployment income end date',
            value: !isNil(borrower.financialProfile?.unemploymentEndDate)
              ? borrower.financialProfile.unemploymentEndDate
              : '-',
          },
          {
            label: 'Other income',
            value: !isNil(
              borrower.financialProfile?.expectedNonUnemploymentIncome,
            )
              ? dollars(borrower.financialProfile.expectedNonUnemploymentIncome)
              : '-',
          },
        ],
      ],
    },
  ];
};

export const borrowerSpouseInfo = ({ borrower }: any) => {
  return [
    {
      title: 'Spouse Info',
      data: [
        [
          {
            label: 'Federal student loans',
            value: isNil(borrower.financialProfile?.spouseHasFsl)
              ? '-'
              : yesOrNo(borrower.financialProfile.spouseHasFsl),
          },
          {
            label: 'Federal loan balance',
            value: isNil(borrower.financialProfile?.spouseCurrentLoanBalance)
              ? '-'
              : dollars(borrower.financialProfile.spouseCurrentLoanBalance),
          },
          {
            label: 'Income change?',
            value: !isNil(borrower.financialProfile?.spouseIncomeChange)
              ? capitalize(borrower.financialProfile.spouseIncomeChange)
              : '-',
          },
          {
            label: 'AGI (adjusted)',
            value: !isNil(borrower.financialProfile?.spouseAdjustedGrossIncome)
              ? dollars(borrower.financialProfile.spouseAdjustedGrossIncome)
              : '-',
          },
          {
            label: 'AGI (expected)',
            value: !isNil(
              borrower.financialProfile?.spouseExpectedAnnualGrossIncome,
            )
              ? dollars(
                  borrower.financialProfile.spouseExpectedAnnualGrossIncome,
                )
              : '-',
          },
          {
            label: 'Spouse employed?',
            value: !isNil(borrower.financialProfile?.spouseCurrentlyEmployed)
              ? yesOrNo(borrower.financialProfile.spouseCurrentlyEmployed)
              : '-',
          },
          {
            label: 'Pay frequency',
            value: !isNil(borrower.financialProfile?.spousePayFrequency)
              ? capitalize(borrower.financialProfile.spousePayFrequency)
              : '-',
          },
          {
            label: '',
            value: '',
          },
          {
            label: '',
            value: '',
          },
        ],
      ],
    },
  ];
};

export const borrowerSpouseUnemploymentInfo = ({ borrower }: any) => {
  return [
    {
      title: 'Spouse Unemployment Info',
      data: [
        [
          {
            label: 'Unemployment income',
            value: !isNil(borrower.financialProfile?.spouseUnemploymentIncome)
              ? dollars(borrower.financialProfile.spouseUnemploymentIncome * 12)
              : '-',
          },
          {
            label: 'Monthly unemployment income',
            value: !isNil(borrower.financialProfile?.spouseUnemploymentIncome)
              ? dollars(borrower.financialProfile.spouseUnemploymentIncome)
              : '-',
          },
          {
            label: 'Unemployment income end date',
            value: !isNil(borrower.financialProfile?.spouseUnemploymentEndDate)
              ? borrower.financialProfile.spouseUnemploymentEndDate
              : '-',
          },
          {
            label: 'Other income',
            value: !isNil(
              borrower.financialProfile?.spouseExpectedNonUnemploymentIncome,
            )
              ? dollars(
                  borrower.financialProfile.spouseExpectedNonUnemploymentIncome,
                )
              : '-',
          },
        ],
      ],
    },
  ];
};

export const borrowerIDRFormSummaryData = ({ borrower }: any) => {
  return (borrower.forms as any[])
    .filter(f => isIdr(f.formType))
    .map(form => ({
      ...form,
      firstName: borrower.profile.firstName,
      lastName: borrower.profile.lastName,
      borrowerId: borrower.id,
    }));
};
export const borrowerPSLFFormSummaryData = ({ borrower }: any) => {
  return (borrower.forms as any[])
    .filter(f => isPslf(f.formType))
    .map(form => ({
      ...form,
      firstName: borrower.profile.firstName,
      lastName: borrower.profile.lastName,
      borrowerId: borrower.id,
    }));
};
export const borrowerRecommendPlansData = ({ borrower }: any) => {
  const getPaths = () => {
    const selectedPaths: any[] = [];
    const currentPaths: any[] = [];

    (borrower?.forms as any[])?.forEach(form => {
      const currentPathId = form?.form?.currentPath?.id;
      const selectedPathId = form?.form?.selectedPath?.id;

      if (!selectedPaths.includes(selectedPathId)) {
        selectedPaths.push(selectedPathId);
      }

      if (!currentPaths.includes(currentPathId)) {
        currentPaths.push(currentPathId);
      }
    });
    return {
      selectedPaths,
      currentPaths,
    };
  };

  const getReason = () => {
    const batchResult = borrower?.pathBatch?.blockedReason
      ? {
          blockedReason: borrower?.pathBatch?.blockedReason,
          batchId: borrower?.pathBatch.id,
        }
      : null;
    const pathResult = (borrower?.forms as any[])
      ?.filter(form => isIdr(form?.formType))
      ?.map(form => ({
        blockedReason: form?.form?.selectedPath?.blockedReason,
        pathId: form?.form?.selectedPath?.id,
      }));
    const reasons =
      !batchResult || !pathResult
        ? []
        : ([batchResult, ...pathResult] as any[]).map(reason => ({
            blockedReason: reason?.blockedReason,
            batchId: reason?.batchId ?? '-',
            pathId: reason?.pathId ?? '-',
          }));
    return reasons;
  };

  const paths = !isEmpty(getReason())
    ? getReason()
    : (borrower?.pathBatch?.paths as any[])?.map(item => {
        return {
          planType: !isNil(item.planType) ? item.planType[0] : '-',
          isSelected: getPaths().selectedPaths.includes(item.id) ? 'Yes' : '-',
          isCurrent: getPaths().currentPaths.includes(item.id) ? 'Yes' : '-',
          firstPayment: !isNil(item.firstPayment)
            ? dollars(item.firstPayment)
            : '-',
          lastPayment: !isNil(item.lastPayment)
            ? dollars(item.lastPayment)
            : '-',
          totalPayment: !isNil(item.totalPayment)
            ? dollars(item.totalPayment)
            : '-',
          amountForgiven: !isNil(item.amountForgiven)
            ? dollars(item.amountForgiven)
            : '-',
          noOfPayments: !isNil(item.numPayments) ? item.numPayments : '-',
          consolidation: isNil(item.requiresConsolidation)
            ? '-'
            : yesOrNo(item.requiresConsolidation),
          withPSLF: isNil(item.isPSLF) ? '-' : yesOrNo(item.isPSLF),
          appType: isNil(item.appType) ? '-' : item.appType,
          recommendationReasons: isNil(item.recommendedReasons)
            ? '-'
            : item.recommendedReasons,
        };
      });
  return sortBy(paths, ['isSelected', 'isCurrent']).reverse();
};

type SaveTransitionPath = {
  id?: number | string | null;
  planType?: string[] | null;
  firstPayment?: number | null;
  savePaymentIfYouDontSwitch?: number | null;
};

type SaveTransitionForm = {
  form?: { currentPath?: { id?: number | string | null } | null } | null;
};

export type SaveTransitionBorrower = {
  forms?: SaveTransitionForm[] | null;
  pathBatch?: { paths?: SaveTransitionPath[] | null } | null;
};

export const borrowerSaveTransitionData = ({
  borrower,
}: {
  borrower: SaveTransitionBorrower;
}) => {
  const currentPathIds = new Set(
    (borrower.forms ?? [])
      .map(form => form.form?.currentPath?.id)
      .filter((id): id is number | string => !isNil(id)),
  );

  const currentPath = (borrower.pathBatch?.paths ?? []).find(
    path => !isNil(path.id) && currentPathIds.has(path.id),
  );

  const planType = currentPath?.planType;
  const isSaveImpacted =
    Array.isArray(planType) && planType.includes(SAVE_PLAN_TYPE);

  if (!currentPath || !isSaveImpacted) {
    return null;
  }

  const paymentOrUnavailable = (amount: number | null | undefined): string =>
    isNil(amount) ? 'Unavailable' : (dollars(amount) as string);

  return [
    {
      title: 'Payment Details',
      data: [
        [
          {
            label: 'Current monthly payment',
            value: paymentOrUnavailable(currentPath.firstPayment),
          },
          {
            label: "Payment if you don't switch",
            value: paymentOrUnavailable(currentPath.savePaymentIfYouDontSwitch),
          },
        ],
      ],
    },
  ];
};

export type SaveTransitionDisplayData = ReturnType<
  typeof borrowerSaveTransitionData
>;
